import { useEffect, useState } from "react";
import type { CogOutputSchema } from "../../types";
import { PredictionDefaultOutput } from "./prediction-output";

/**
 * Returns true if the schema indicates that the model supports
 * streaming the file streaming API. This logic lives in
 * replicate/api.
 *
 * Callers still need to check that the Prediction has a `urls.stream`
 * property in order to actually connect to the stream.
 */
export function isFileStreamingModel(schema: CogOutputSchema): boolean {
  // biome-ignore lint/complexity/useLiteralKeys:
  if (schema["type"] === "string" && schema["format"] === "uri") {
    return true;
  }

  return (
    // biome-ignore lint/complexity/useLiteralKeys:
    schema["type"] === "array" &&
    // biome-ignore lint/complexity/useLiteralKeys:
    schema["items"]?.type === "string" &&
    // biome-ignore lint/complexity/useLiteralKeys:
    schema["items"]?.format === "uri"
  );
}

export function PredictionFileStreamingOutput({
  schema,
  streamUrl,
}: { streamUrl: string; schema: CogOutputSchema }) {
  const [output, setOutput] = useState<string[]>([]);

  useEffect(() => {
    let controller: AbortController;
    (async () => {
      let url = streamUrl;
      while (true) {
        controller = new AbortController();
        const res = await fetch(url, { signal: controller.signal });
        if (!res.ok) {
          if (res.status === 404) {
            // End of stream;
            return;
          }
          console.error(
            new Error(`Failed to fetch ${url} response status: ${res.status}`)
          );
          return;
        }

        if (res.status === 204) {
          // Skipped upload fallback to the URL.
          const src = res.headers.get("Location");
          if (src) {
            setOutput((state) => [...state, src]);
          }
          return;
        }

        const src = URL.createObjectURL(await res.blob());
        setOutput((state) => [...state, src]);

        const nextUrl = res.headers.get("Next");
        if (!nextUrl) {
          console.warn("missing next header in response");
          return;
        }
        url = nextUrl;
      }
    })().catch((err) => console.error(err));

    return () => controller.abort();
  }, [streamUrl]);

  return (
    <PredictionDefaultOutput
      output={output}
      alwaysRenderURLsAsDownload={false}
      schema={schema}
      status="processing"
      reportFallback={false}
    />
  );
}
